import fetchWithAuth from "@/api/secureFetch";
import {
    ContextMeshApiError,
    parseRecommendation,
    parseSourceDiagnostic,
    type ContextMeshCategoryKey,
    type ContextMeshRecommendation,
    type ContextMeshSourceDiagnostic
} from "./contextMeshAnalysis";

export type ContextMeshReportItemStatus = "rated" | "not_rated" | "processing" | "failed";
export type ContextMeshReportRelevance = "relevant" | "low_relevance" | "not_rated";

export interface ContextMeshEvidenceCounts {
    analyzed: number;
    qualified: number;
    selected: number;
    requiredCapabilities: number;
    coveredCapabilities: number;
}

export interface ContextMeshReportItem {
    id: string;
    type: string;
    parentId: string | null;
    name: string;
    path: string | null;
    score: number | null;
    status: ContextMeshReportItemStatus;
    relevance: ContextMeshReportRelevance;
    relevanceReason: string;
    currentState: string;
    method: string;
    recommendation: ContextMeshRecommendation | null;
    hasChildren: boolean;
    childCount: number;
    evidenceCounts?: ContextMeshEvidenceCounts;
}

export interface ContextMeshReportPage {
    productId: number | null;
    contextMeshId?: string | null;
    runId: string;
    scoringVersion: string;
    formulaVersion: string;
    category: ContextMeshCategoryKey;
    categoryScore: number;
    method: string;
    sourceDiagnostic?: ContextMeshSourceDiagnostic | null;
    items: ContextMeshReportItem[];
    nextCursor: string | null;
}

export type ContextMeshReportTarget =
    | { productId: number; contextMeshId?: never }
    | { contextMeshId: string; productId?: never };

export interface ContextMeshReportQuery {
    category: ContextMeshCategoryKey;
    parentType?: string;
    parentId?: string;
    cursor?: string;
    limit?: number;
}

function isRecord(value: unknown): value is Record<string, unknown> {
    return typeof value === "object" && value !== null && !Array.isArray(value);
}

function requiredString(value: unknown, field: string): string {
    if (typeof value !== "string" || !value.trim()) throw new Error(`Invalid Context Mesh report: ${field}`);
    return value;
}

function requiredNumber(value: unknown, field: string): number {
    if (typeof value !== "number" || !Number.isFinite(value)) throw new Error(`Invalid Context Mesh report: ${field}`);
    return value;
}

function parseEvidenceCounts(value: unknown, field: string): ContextMeshEvidenceCounts {
    if (!isRecord(value)) throw new Error(`Invalid Context Mesh report: ${field}`);
    const counts: ContextMeshEvidenceCounts = {
        analyzed: requiredNumber(value.analyzed, `${field}.analyzed`),
        qualified: requiredNumber(value.qualified, `${field}.qualified`),
        selected: requiredNumber(value.selected, `${field}.selected`),
        requiredCapabilities: requiredNumber(value.requiredCapabilities, `${field}.requiredCapabilities`),
        coveredCapabilities: requiredNumber(value.coveredCapabilities, `${field}.coveredCapabilities`)
    };
    if (Object.values(counts).some(count => !Number.isInteger(count) || count < 0)) {
        throw new Error(`Invalid Context Mesh report: ${field}`);
    }
    if (
        counts.qualified > counts.analyzed
        || counts.selected > counts.qualified
        || counts.coveredCapabilities > counts.requiredCapabilities
    ) {
        throw new Error(`Invalid Context Mesh report: ${field}`);
    }
    return counts;
}

function parseItem(value: unknown, index: number): ContextMeshReportItem {
    if (!isRecord(value)) throw new Error(`Invalid Context Mesh report: items[${index}]`);
    const score = value.score === null ? null : requiredNumber(value.score, `items[${index}].score`);
    const childCount = requiredNumber(value.childCount, `items[${index}].childCount`);
    const status = requiredString(value.status, `items[${index}].status`) as ContextMeshReportItemStatus;
    const relevance = requiredString(value.relevance, `items[${index}].relevance`) as ContextMeshReportRelevance;
    if (score !== null && (score < 0 || score > 100)) throw new Error(`Invalid Context Mesh report: items[${index}].score`);
    if (!Number.isInteger(childCount) || childCount < 0) throw new Error(`Invalid Context Mesh report: items[${index}].childCount`);
    if (!["rated", "not_rated", "processing", "failed"].includes(status)) throw new Error(`Invalid Context Mesh report: items[${index}].status`);
    if (!["relevant", "low_relevance", "not_rated"].includes(relevance)) throw new Error(`Invalid Context Mesh report: items[${index}].relevance`);
    if (typeof value.hasChildren !== "boolean") throw new Error(`Invalid Context Mesh report: items[${index}].hasChildren`);
    if (!value.hasChildren && childCount !== 0) throw new Error(`Invalid Context Mesh report: items[${index}].childCount`);
    const recommendation = value.recommendation == null ? null : parseRecommendation(value.recommendation);
    if (value.recommendation != null && recommendation === null) throw new Error(`Invalid Context Mesh report: items[${index}].recommendation`);
    const item: ContextMeshReportItem = {
        id: requiredString(value.id, `items[${index}].id`),
        type: requiredString(value.type, `items[${index}].type`),
        parentId: value.parentId == null ? null : requiredString(value.parentId, `items[${index}].parentId`),
        name: requiredString(value.name, `items[${index}].name`),
        path: value.path == null ? null : requiredString(value.path, `items[${index}].path`),
        score,
        status,
        relevance,
        relevanceReason: typeof value.relevanceReason === "string" ? value.relevanceReason : "",
        currentState: requiredString(value.currentState, `items[${index}].currentState`),
        method: requiredString(value.method, `items[${index}].method`),
        recommendation,
        hasChildren: value.hasChildren,
        childCount
    };
    if (value.evidenceCounts != null) {
        item.evidenceCounts = parseEvidenceCounts(value.evidenceCounts, `items[${index}].evidenceCounts`);
    }
    return item;
}

export function parseContextMeshReport(payload: unknown): ContextMeshReportPage {
    if (!isRecord(payload)) throw new Error("Invalid Context Mesh report: root");
    const category = requiredString(payload.category, "category") as ContextMeshCategoryKey;
    if (!["discover", "external", "file", "code"].includes(category)) throw new Error("Invalid Context Mesh report: category");
    const categoryScore = requiredNumber(payload.categoryScore, "categoryScore");
    if (categoryScore < 0 || categoryScore > 100) throw new Error("Invalid Context Mesh report: categoryScore");
    if (!Array.isArray(payload.items)) throw new Error("Invalid Context Mesh report: items");
    return {
        productId: payload.productId == null ? null : requiredNumber(payload.productId, "productId"),
        contextMeshId: payload.contextMeshId == null ? null : requiredString(payload.contextMeshId, "contextMeshId"),
        runId: requiredString(payload.runId, "runId"),
        scoringVersion: requiredString(payload.scoringVersion, "scoringVersion"),
        formulaVersion: requiredString(payload.formulaVersion, "formulaVersion"),
        category,
        categoryScore,
        method: requiredString(payload.method, "method"),
        sourceDiagnostic: parseSourceDiagnostic(payload.sourceDiagnostic, "sourceDiagnostic"),
        items: payload.items.map(parseItem),
        nextCursor: payload.nextCursor == null ? null : requiredString(payload.nextCursor, "nextCursor")
    };
}

export async function fetchContextMeshReport(
    token: string,
    target: ContextMeshReportTarget | number,
    query: ContextMeshReportQuery,
    signal?: AbortSignal
): Promise<ContextMeshReportPage> {
    const params = new URLSearchParams({ category: query.category, limit: String(query.limit ?? 50) });
    if (query.parentType && query.parentId) {
        params.set("parentType", query.parentType);
        params.set("parentId", query.parentId);
    }
    if (query.cursor) params.set("cursor", query.cursor);
    let response: Response;
    try {
        const normalizedTarget: ContextMeshReportTarget = typeof target === "number"
            ? { productId: target }
            : target;
        const path = "productId" in normalizedTarget
            ? `/api/products/${normalizedTarget.productId}/context-analysis/report`
            : `/api/context-meshes/${normalizedTarget.contextMeshId}/context-analysis/report`;
        response = await fetchWithAuth(`${path}?${params.toString()}`, token, {
            headers: { Accept: "application/json" },
            signal
        });
    } catch (reason) {
        if (reason instanceof DOMException && reason.name === "AbortError") throw reason;
        throw new ContextMeshApiError(503, { code: "REPORT_UNAVAILABLE", message: "The score detail report is temporarily unavailable." });
    }
    let payload: unknown;
    try {
        payload = await response.json();
    } catch {
        throw new ContextMeshApiError(response.status || 503, { code: "INVALID_RESPONSE", message: "The score detail report returned an invalid response." });
    }
    if (!response.ok) {
        const error = isRecord(payload) && isRecord(payload.error) ? payload.error : {};
        throw new ContextMeshApiError(response.status, {
            code: typeof error.code === "string" ? error.code : "REPORT_UNAVAILABLE",
            message: typeof error.message === "string" ? error.message : "The score detail report is temporarily unavailable."
        });
    }
    try {
        const report = parseContextMeshReport(payload);
        const normalizedTarget: ContextMeshReportTarget = typeof target === "number"
            ? { productId: target }
            : target;
        const identityMatches = "productId" in normalizedTarget
            ? report.productId === normalizedTarget.productId && report.contextMeshId == null
            : report.contextMeshId === normalizedTarget.contextMeshId && report.productId === null;
        if (!identityMatches || report.category !== query.category) throw new Error("Mismatched report provenance");
        return report;
    } catch {
        throw new ContextMeshApiError(503, { code: "INVALID_RESPONSE", message: "The score detail report returned an invalid response." });
    }
}
